'use client';

import classNames from 'classnames';
import Link from 'next/link';
import { ChangeEvent, FC, FormEvent, useEffect, useState } from 'react';

import { joinEventWaitListApi } from '@/entities/event/api';
import {
  EventFormValue,
  eventFormSchema,
} from '@/features/event/model/formSchema';
import EventFormFields from '@/features/event/ui/event-form-fields/EventFormFields';
import { useUserStore } from '@/features/user';
import { extractFieldErrors, usePhoneMask } from '@/shared/lib';
import { Checkbox } from '@/shared/ui';
import { MODAL_TYPES } from '@/widgets/modals/config/constants';
import { useModalStore, type EventModalPayload } from '@/widgets/modals/model/store';

import styles from './eventWaitListRequestForm.module.scss';

interface EventFormProps {
  event: EventModalPayload;
  variant?: 'modal' | 'inline';
}

const EventWaitListRequestForm: FC<EventFormProps> = ({
  event,
  variant = 'modal',
}) => {
  const isInline = variant === 'inline';
  const { openModal } = useModalStore();
  const { isAuth, user } = useUserStore();
  const [form, setForm] = useState<EventFormValue>({
    name: '',
    phone: '',
    email: '',
    policy: false,
  });
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof EventFormValue, string>>
  >({});
  const { phone, handlePhonePast, onChange: setPhone } = usePhoneMask();

  useEffect(() => {
    if (!isAuth || !user) return;

    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');

    setForm((prev) => ({
      ...prev,
      name: prev.name || fullName,
      email: prev.email || user.email,
    }));

    if (!phone && user.phone) {
      handlePhonePast(user.phone);
    }
  }, [handlePhonePast, isAuth, phone, user]);

  const normalizePhone = (phoneValue: string) => {
    const digits = phoneValue.replace(/\D/g, '');
    return `+${digits}`;
  };

  const handleSubmit = async (submitEvent: FormEvent<HTMLFormElement>) => {
    submitEvent.preventDefault();

    try {
      await eventFormSchema.validate({ ...form, phone }, { abortEarly: false });
      await joinEventWaitListApi(event.slug, {
        name: form.name.trim(),
        phone: normalizePhone(phone),
        email: form.email.trim(),
      });
      setFieldErrors({});
      openModal(MODAL_TYPES.eventWaitlistSuccess);
    } catch (error) {
      setFieldErrors(
        extractFieldErrors(error) as Partial<
          Record<keyof EventFormValue, string>
        >,
      );
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFieldErrors((prev) => ({ ...prev, phone: '' }));
    setPhone(event);
  };

  return (
    <form
      className={classNames(styles.form, isInline && styles.form_inline)}
      onSubmit={handleSubmit}
    >
      {!isInline && (
        <div className={styles.form__headBlock}>
          <p className={styles.form__head}>{'Лист ожидания на мероприятие'}</p>
          <p className={styles.form__head}>{event.title}</p>
        </div>
      )}

      <div className={styles.form__descriptionBlock}>
        <p className={styles.form__description}>
          К сожалению, на данный момент свободных мест нет. Оставьте ваши
          контакты, и мы свяжемся с вами, если кто-то отменит регистрацию
        </p>
      </div>

      <div className={styles.form__subtitleBlock}>
        <p className={styles.form__subtitle}>Личные данные</p>
      </div>

      <EventFormFields
        className={styles.form__fields}
        email={form.email}
        emailError={fieldErrors.email}
        name={form.name}
        nameError={fieldErrors.name}
        phone={phone}
        phoneError={fieldErrors.phone}
        onChange={handleInputChange}
        onPhoneChange={handlePhoneChange}
      />

      <button
        className={classNames(
          'button',
          isInline ? 'button--confirm-black' : 'button--confirm-white',
          styles.form__button,
          isInline && styles.form__button_inline,
        )}
      >
        Отправить
      </button>

      <Checkbox
        checked={form.policy}
        className={styles.form__checkbox}
        error={fieldErrors.policy}
        variant={isInline ? 'grey' : 'white'}
        onChange={(value) => {
          setFieldErrors((prev) => ({ ...prev, policy: '' }));
          setForm((prev) => ({ ...prev, policy: value }));
        }}
      >
        Согласие с {''}
        <Link
          className={classNames('link link--underline')}
          href="/page/politika-konfidentsialnosti"
        >
          Политикой конфиденциальности
        </Link>
      </Checkbox>
    </form>
  );
};

export default EventWaitListRequestForm;
